import { ACL_TYPE } from '@openx/types/aclType.ts';
import type { Change, IncrementalChange } from '@openx/types/auditTrail.ts';
import type { ConstValues } from '@openx/types/helpers.ts';
import type { ResourceOperationType } from '@openx/types/resources.ts';
import type { JSX, ReactNode } from 'react';

export type ResourceLabels<R extends object = object> = {
  [key in keyof R]?: string;
};

export type StaticValueMapping = {
  [key in string]: string | JSX.Element;
};

export type DynamicValueMapping = (value: string) => string | JSX.Element | undefined;

export type ValueMapping = StaticValueMapping | DynamicValueMapping;

export type FieldsValuesMappings<R extends {}> = {
  [key in keyof R]?: ValueMapping;
};

export type LabeledChange<T extends Change = Change> = {
  Element?: ReactNode;
  OldValueElement?: ReactNode;
  label: string;
  oldValue?: ChangedValue | Required<IncrementalChange<Change>>;
} & T;

export interface FieldsChanges {
  [field: string]: LabeledChange;
}

export interface HistoryEntryData {
  who: string;
  when: string;
  what: FieldsChanges;
  mode: ResourceOperationType;
  objectId: number | string;
  traceId?: string;
}

export interface GroupedChanges {
  groupTitle: string;
  changes: LabeledChange[];
}

export interface HistoryEntryDataGrouped {
  who: string;
  when: string;
  what: GroupedChanges[];
  mode: ResourceOperationType;
  objectId: number | string;
  traceId?: string;
}

export interface HistoryEntriesGroup {
  groupTitle: string;
  keys: string[];
}

export type GroupHistoryEntriesHandler<G extends HistoryEntriesGroup> = (
  entry: HistoryEntryData,
  groups: G[]
) => HistoryEntryDataGrouped;

export type HistoryData = {
  create: HistoryEntryData[] | HistoryEntryDataGrouped[];
  update: HistoryEntryDataGrouped[];
};

export type ChangedValue = null | string;

export const ChangesGroups = {
  BidRequests: 'bid_requests',
  Details: 'details',
  DiscountDetails: 'discount_details',
  DiscountSettings: 'discount_settings',
  DiscountType: 'discount_type',
  FeeDetails: 'fee_details',
  PrioritizeAndFiltering: 'prioritize_and_filtering',
  SpendCap: 'spend_cap',
  Targeting: 'targeting',
  Usage: 'usage',
} as const;

export type ChangesGroupsValues = ConstValues<typeof ChangesGroups>;
export type GroupedChangesMap = { [K in ChangesGroupsValues]?: GroupedChanges };
export type ValuesOptionsMap = { [key: string]: string | Record<string, string> };

export type ChangedValueRenderConfig<T> = {
  group: ChangesGroupsValues;
  regExps: RegExp[];
  render?: (
    newValue: ChangedValue,
    path: string,
    historyLabelsMap: Record<string, string>,
    valueMappings?: ValuesOptionsMap,
    oldValue?: ChangedValue,
    shouldRenderBothValues?: boolean
  ) => ReactNode;
  renderIncrementalChange?: (
    change: Required<IncrementalChange<T>>,
    path: string,
    historyLabelsMap: Record<string, string>,
    valueMappings?: ValuesOptionsMap,
    oldValue?: Required<IncrementalChange<T>>,
    shouldRenderBothValues?: boolean
  ) => ReactNode;
};

export type HistoryRenderConfig<T = string> = {
  changedValuesRenderConfig: ChangedValueRenderConfig<T>[];
  changesToExclude?: string[];
  groupedChangesMap: GroupedChangesMap;
  historyLabelsMap: Record<string, string>;
  incrementalValuesList?: string[];
  valueOptionsMap?: ValuesOptionsMap;
};

export type SelectedChanges = Record<string, ChangedValue | Required<IncrementalChange<Change>>>;

export const AccountAcl = {
  ACL_DMP_READ: `acl.${ACL_TYPE.DMP_SOURCES_READ}`,
  ACL_FLOORRULE_READ: `acl.${ACL_TYPE.FLOORRULE_READ}`,
  ACL_INTERNAL_EXT_INVENTORY_MAP_READ: `acl.${ACL_TYPE.EXT_INVENTORY_MAP_READ}`,
  ACL_PMP_READ: `acl.${ACL_TYPE.DEAL_READ}`,
  ACL_THIRD_PARTY_FEES_PLATFORM_SHARE_READ: `acl.${ACL_TYPE.THIRD_PARTY_FEES_PLATFORM_SHARE_READ}`,
  ACL_THIRD_PARTY_FEES_PLATFORM_SHARE_WRITE: `acl.${ACL_TYPE.THIRD_PARTY_FEES_PLATFORM_SHARE_WRITE}`,
  ACL_THIRD_PARTY_FEES_READ: `acl.${ACL_TYPE.THIRD_PARTY_FEES_READ}`,
  ACL_THIRD_PARTY_FEES_WRITE: `acl.${ACL_TYPE.THIRD_PARTY_FEES_WRITE}`,
} as const;
export type AccountAcl = ConstValues<typeof AccountAcl>;
